import { DescriptionsItemType } from 'antd/es/descriptions';
import { useTranslation } from 'translations/hooks';

import { formatReportDateStringToLocalizedDateStringWithDayOfWeek } from 'business/shift-report/report/services/timeOperations';
import { GetShiftReportPdfMetadataQuery } from 'generated/graphql';
import { useMediaType } from 'technical/media/hooks';
import { notEmpty } from 'technical/utils/not-empty';

type UseBuildMetadataDescriptionItems = {
  report: NonNullable<GetShiftReportPdfMetadataQuery['shiftReport']>;
  showMoreOnMobile?: boolean;
};

export const useBuildMetadataDescriptionItems = ({
  report,
  showMoreOnMobile = false,
}: UseBuildMetadataDescriptionItems) => {
  const { t } = useTranslation();
  const { isMobile } = useMediaType();

  const firstRowSpan = 2;
  const firstRow = [
    {
      key: 'date',
      label: t('pages.form.date.label'),
      children: formatReportDateStringToLocalizedDateStringWithDayOfWeek(
        report.date,
      ),
      className: 'item',
      span: firstRowSpan,
    },
    {
      key: 'shift',
      label: t('pages.form.shift.label'),
      children: report.shift.name,
      className: 'item',
      span: firstRowSpan,
    },
    {
      key: 'creator',
      label: t('pages.form.creator.label'),
      children: report.userName,
      className: 'item',
      span: firstRowSpan,
    },
  ] satisfies DescriptionsItemType[];

  const secondRowSpan = report.team ? 2 : 3;
  const secondRow = [
    report.team
      ? {
          key: 'team',
          label: t('pages.form.team.label'),
          children: report.team.name,
          className: 'item',
          span: secondRowSpan,
        }
      : undefined,
    {
      key: 'operator',
      label: t('pages.form.operator.label'),
      children: report.operator?.name,
      className: 'item',
      span: secondRowSpan,
    },
    {
      key: 'shiftLeader',
      label: t('pages.form.shiftLeader.label'),
      children: report.shiftLeader?.name,
      className: 'item',
      span: secondRowSpan,
    },
  ].filter(notEmpty) satisfies DescriptionsItemType[];

  const thirdRowSpan = report.shiftManager && report.shiftEngineer ? 3 : 6;
  const thirdRow = [
    report.shiftEngineer
      ? {
          key: 'shiftEngineer',
          label: t('pages.form.shiftEngineer.label'),
          children: `${report.shiftEngineer.firstName} ${report.shiftEngineer.lastName}`,
          className: 'item',
          span: thirdRowSpan,
        }
      : undefined,
    report.shiftManager
      ? {
          key: 'shiftManager',
          label: t('pages.form.shiftManager.label'),
          children: `${report.shiftManager.firstName} ${report.shiftManager.lastName}`,
          className: 'item',
          span: thirdRowSpan,
        }
      : undefined,
  ].filter(notEmpty) satisfies DescriptionsItemType[];

  const items = [
    ...firstRow,
    ...(isMobile && !showMoreOnMobile ? [] : secondRow),
    ...thirdRow,
  ] satisfies DescriptionsItemType[];

  return items;
};
